<style lang="scss" scoped>
.zoezi-show-receipt-dialog {
    .list-unstyled {
        list-style: none;
    }

    tr.empty-row {
        height: 10px;
    }

    table.table-header tr td:last-child {
        padding-left: 10px;
    }

    table.table-header tr td:first-child, .bold {
        font-weight: bold;
    }
}
</style>

<template>
    <div>
        <v-expansion-panels v-model="expandedPanel">
            <v-expansion-panel class="no-elevation" >
                <v-expansion-panel-header>
                    <div class="zoezi-activity-section">
                        <h3>{{$translate('Receipts')}}</h3>
                        <small>{{$translate("Everything you've bought is listed here")}}</small>
                    </div>
                    <v-badge color="grey" :content="String(receipts.length)"></v-badge>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div v-if="receipts.length == 0">
                        {{$translate('No receipts yet')}}
                    </div>
                    <payment-card v-for="receipt in receipts" :key="receipt.id != null ? 'receipt-' + receipt.id : 'transaction-' + receipt.transaction_id" :item="receipt" @content-click="showReceipt(receipt)">
                        <template #date-line>
                            <div style="font-size: 11px" class="grey--text">{{$translate('Paid')}}: {{Date.formatFullShorter(receipt.date)}}</div>
                        </template>
                        <template #actions>
                            <v-btn icon @click.stop="showReceipt(receipt)" color="primary" small>
                                <v-icon>mdi-information-outline</v-icon>
                            </v-btn>
                            <v-btn small class="primary mb-2 zoezi-button-thin" :loading="receipt.loading" @click.stop="getPdf(receipt)">
                                <v-icon small class="mr-1">mdi-download</v-icon>
                                {{$translate('Receipt')}}
                            </v-btn>
                            <v-btn v-if="canWithdraw(receipt)" small color="error" class="mb-2 zoezi-button-thin" @click.stop="withdrawalEntry = receipt">{{ $translate('Withdraw from purchase') }}</v-btn>
                        </template>
                    </payment-card>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-if="showReceiptDialog" v-model="showReceiptDialog" scrollable max-width="850px">
            <v-card tile elevation="0" class="zoezi-show-receipt-dialog">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    {{showingReceipt.name}}
                    <v-spacer />
                    <v-btn icon @click="showReceiptDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col md=6 cols=12>
                            <table class="table-header">
                                <tr><td>{{'Status' | translate}}</td><td>{{$translate('Paid')}}</td></tr>
                                <tr><td>{{'Date' | translate}}</td><td>{{showingReceipt.date.yyyymmdd()}}</td></tr>
                                <tr v-if="showingReceipt.method"><td>{{'Payment method' | translate}}</td><td>{{$translate(showingReceipt.method).capitalize()}}</td></tr>
                                <tr class="empty-row"></tr>
                                <tr><td>{{'Amount' | translate}}</td><td class="bold">{{showingReceipt.amount | price}}</td></tr>
                            </table>
                        </v-col>

                        <v-col md=6 cols=12>
                            <ul class="list-unstyled pl-0">
                                <li><p class="bold">{{'This payment contains the following products' | translate}}</p></li>
                                <li v-for="(item,index) in showingReceipt.items" :key="index">
                                    {{item.name}}
                                    <withdrawal-status v-if="item.withdrawalRequest" :text="withdrawalRequestText(item.withdrawalRequest)" />
                                </li>
                            </ul>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-btn color="primary" :loading="showingReceipt.loading" @click="getPdf(showingReceipt)">{{$translate('Receipt')}}</v-btn>
                    <v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <withdrawal-my-page-dialog v-if="withdrawalEntry" :entry="withdrawalEntry" @close="withdrawalEntry = null" @withdrawn="fetchReceipts" />
    </div>
</template>

<script>
import {downloadFile, isExpoApp, withdrawalRequestText} from '../util.js'
import PaymentCard from '../components/PaymentCard.vue';
import WithdrawalStatus from '../components/WithdrawalStatus.vue';
import WithdrawalMyPageDialog from './WithdrawalMyPageDialog.vue';
export default {
    name: 'zoezi-receiptlist',

    components: { PaymentCard, WithdrawalStatus, WithdrawalMyPageDialog },

    zoezi: {
        title: 'Receipt list',
        icon: 'mdi-receipt'
    },

    data: () => ({
        receipts: [],
        unsubscribe: null,
        expandedPanel: 0,
        showReceiptDialog: false,
        showingReceipt: null,
        withdrawalEntry: null
    }),

    props: {
    },

    methods: {
        withdrawalRequestText,
        canWithdraw(entry) {
            return this.$api.hasFlag('flag-withdrawal-request') && (entry.items || []).some(item => item.canWithdraw);
        },
        showReceipt(receipt) {
            this.showingReceipt = receipt;
            this.showReceiptDialog = true;
        },
        fetchReceipts() {
            this.$api.get('/api/memberapi/payments/get').then(x => {
                this.receipts = x.map(r => ({...r, name: r.name || (r.items ? r.items.map(i => i.name).join(', ') : ''), date: Date.newFull(r.date), loading: false})).sort((a, b) => b.date - a.date);
            });
        },
        getPdf(receipt) {
            if (!receipt.id && !receipt.transaction_id) {
                this.$store.commit('showSnackBar', {
                    text: this.$translate('An error occured, please contact the staff'),
                    color: 'error',
                });
                return;
            }

            receipt.loading = true;
            if (isExpoApp() && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openReceipt', data: receipt }));
                receipt.loading = false;
            } else {
                this.$api.get('/api/memberapi/receipt/get', receipt.transaction_id ? {transaction_id: receipt.transaction_id} : {id: receipt.id}, {blob: true}).then(x => {
                    downloadFile({filename: receipt.name + '.pdf', blob: x, type: 'application/pdf'});
                    receipt.loading = false;
                })
            }
        }
    },

    mounted() {
        this.fetchReceipts();
        this.unsubscribe = this.$root.$api.subscribe({type: 'payment', callback: () => this.fetchReceipts()})
    },
    destroyed() {
        this.unsubscribe?.();
    }
}
</script>
